import React, { useState, useEffect } from 'react';
import { Box, Button, Grid, MenuItem, Select, Typography, Pagination } from '@mui/material';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import HeroBanner from './DiscussionBanner';
import DiscussionCard from './DiscussionCard';
import AddDiscussionModal from './AddDiscussionModal';
import { getDiscussions, deleteDiscussion } from '../../api/discussion api';

const Discussions = () => {
  const { data: initialDiscussionData = [], isLoading: isLoadingDiscussions, error: disError } = useQuery({
    queryKey: ['discussions'],
    queryFn: getDiscussions,
  });

  const [discussions, setDiscussions] = useState(initialDiscussionData);
  const [filteredDiscussions, setFilteredDiscussions] = useState(discussions);
  const [open, setOpen] = useState(false);
  const [newTopicInput, setNewTopicInput] = useState('');
  const [filterType, setFilterType] = useState('recent');
  const [activeTab, setActiveTab] = useState('Recent');
  const [currentPage, setCurrentPage] = useState(1);
  const discussionsPerPage = 3;

  const user = useSelector((state) => state.users);
  const [newDiscussion, setNewDiscussion] = useState({
    title: '',
    userId: user.id,
    description: '',
    author: 'CurrentUser',
    authorImage: user?.profileImage,
    date: new Date().toLocaleDateString(),
    views: 0,
    likes: 0,
    comments: [],
    topic: '',
    isPinned: false, 
  });
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleAddDiscussion = () => {
    if (newDiscussion.title.trim() && newDiscussion.description.trim()) {
      const updatedDiscussion = {
        ...newDiscussion,
        id: discussions?.length + 1,
        topic: newTopicInput || newDiscussion.topic,
      };
      setDiscussions([...discussions, updatedDiscussion]);
      setFilteredDiscussions([...filteredDiscussions, updatedDiscussion]);
      resetNewDiscussion();
      handleClose();
      toast.success("Discussion added successfully!"); 
    }
  };

  const resetNewDiscussion = () => {
    setNewDiscussion({
      title: '',
      description: '',
      userId: '',
      author: 'CurrentUser',
      authorImage: 'path-to-current-user-image',
      date: new Date().toLocaleDateString(),
      views: 0,
      likes: 0,
      comments: [],
      topic: '',
      isPinned: false, 
    });
    setNewTopicInput('');
  };

  useEffect(() => {
    if (initialDiscussionData) {
      setDiscussions(initialDiscussionData);
      setFilteredDiscussions(initialDiscussionData);
    }
  }, [initialDiscussionData]);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);

    const filters = {
      Recent: () => [...discussions].sort((a, b) => new Date(b.date) - new Date(a.date)),
      Unanswered: () => discussions.filter((d) => d.comments?.length === 0),
      Unsolved: () => discussions.filter((d) => !d.isSolved),
      Solved: () => discussions.filter((d) => d.isSolved),
      Pinned: () => discussions.filter((d) => d.isPinned),
    };

    setFilteredDiscussions(filters[tab] ? filters[tab]() : discussions);
  };

  const applySorting = (sortBy) => {
    const sortedDiscussions = sortBy === 'popular'
      ? [...discussions].sort((a, b) => b.likes - a.likes)
      : [...discussions].sort((a, b) => new Date(b.date) - new Date(a.date));
    setFilteredDiscussions(sortedDiscussions);
  };

  const handleSortChange = (event) => {
    const sortBy = event.target.value;
    setFilterType(sortBy);
    applySorting(sortBy);
  };

  const handleDeletePost = async (userId) => {
    try {
      await deleteDiscussion(userId);
      toast.success("Discussion deleted successfully!"); 
      setFilteredDiscussions(filteredDiscussions.filter(discussion => discussion.id !== userId));
    } catch (error) {
      console.error('Error deleting Discussion:', error);
      toast.error("Failed to delete Discussion."); 
    }
  };

  const handlePinToggle = (id) => {
    setDiscussions((prevDiscussions) =>
      prevDiscussions.map((discussion) =>
        discussion.id === id ? { ...discussion, isPinned: !discussion.isPinned } : discussion
      )
    );
    setFilteredDiscussions((prevFiltered) =>
      prevFiltered.map((discussion) =>
        discussion.id === id ? { ...discussion, isPinned: !discussion.isPinned } : discussion
      )
    );
  };

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleFilterByTopic = (topic) => {
    setCurrentPage(1);
    setFilteredDiscussions(topic === 'All' ? discussions : discussions.filter(d => d.topic === topic));
  };

  const indexOfLastDiscussion = currentPage * discussionsPerPage;
  const indexOfFirstDiscussion = indexOfLastDiscussion - discussionsPerPage;
  const currentDiscussions = filteredDiscussions.slice(indexOfFirstDiscussion, indexOfLastDiscussion);
  const totalPages = Math.ceil(filteredDiscussions.length / discussionsPerPage);
 

  const handleSearch = (searchTerm) => {
      const filtered = discussions.filter(discussion => {
          const title = discussion.title.toLowerCase();
          const description = discussion.description.toLowerCase();
          const topic = discussion.topic.toLowerCase();

          return (
              title.includes(searchTerm.toLowerCase()) ||
              description.includes(searchTerm.toLowerCase()) ||
              topic.includes(searchTerm.toLowerCase())
          );
      });

      setFilteredDiscussions(filtered); 
  };
  return (
    <Box>
         <HeroBanner onSearch={handleSearch}  />
      <Box sx={{ padding: '2vh 4vh' }}>
        <Typography variant="h4" gutterBottom color="black">Discussions Forum</Typography>
        <Grid container justifyContent="space-between" alignItems="center" sx={{ marginBottom: '3vh' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
            {['Recent', 'Unanswered', 'Unsolved', 'Solved', 'Pinned'].map((tab) => (
              <Typography
                key={tab}
                variant="body1"
                component="a"
                href="#"
                onClick={() => handleTabClick(tab)}
                sx={{
                  marginRight: '2vw',
                  color: activeTab === tab ? '#1976d2' : '#333',
                  textDecoration: activeTab === tab ? 'underline' : 'none',
                  cursor: 'pointer',
                  transition: 'color 0.3s, transform 0.3s',
                  '&:hover': {
                    color: '#1976d2',
                  }
                }}
              >
                {tab}
              </Typography>
            ))}
            <Select
              value={filterType}
              onChange={handleSortChange}
              sx={{
                height: '40px',
                marginLeft: 'auto',
                color: '#333',
                '& .MuiSelect-select': { padding: '8px 12px' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
              }}
            >
              <MenuItem value="recent">Most Recent</MenuItem>
              <MenuItem value="popular">Most Popular</MenuItem>
            </Select>
          </Box>

          <Button variant="contained" color="primary" onClick={handleOpen}>Add Discussion</Button>
        </Grid>
      </Box>

      <AddDiscussionModal
        open={open}
        handleClose={handleClose}
        newDiscussion={newDiscussion}
        setNewDiscussion={setNewDiscussion}
        handleAddDiscussion={handleAddDiscussion}
        uniqueTopics={['All', ...Array.from(new Set(discussions.map(d => d.topic)))]}
        newTopicInput={newTopicInput}
        setNewTopicInput={setNewTopicInput}
      />

      <Box sx={{ padding: '0.5vh 4vh' }}>
        <Typography variant="h5">Topics</Typography>
        <Grid container spacing={2}>
          {['All', ...Array.from(new Set(discussions.map(d => d.topic)))].map(topic => (
            <Grid item key={topic}>
              <Button variant="outlined" onClick={() => handleFilterByTopic(topic)} sx={{
                transition: 'background 0.3s',
                '&:hover': { background: 'rgba(0, 123, 255, 0.1)' },
              }}>
                {topic}
              </Button>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Grid container spacing={3} sx={{ marginTop: '4vh' }}>
        {currentDiscussions.map((discussion) => (
          <Grid item xs={12} sm={6} md={12} key={discussion.id}>
            <DiscussionCard
             discussion={discussion}
             handleDelete={handleDeletePost}
             onPinToggle={handlePinToggle} 
            />
          </Grid>
        ))}
      </Grid>

      <Pagination
        count={totalPages}
        page={currentPage}
        onChange={handleChangePage}
        color="primary"
        sx={{ marginTop: '2vh' }}
      />
      <ToastContainer />
    </Box>
  );
};

export default Discussions;
